import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RepositoryClient } from '../httpClients/RepositoryClient';
import {
  CreateMarketplaceMTGithubInstallationRequest,
  CreateMarketplaceMTGithubInstallationResponse,
} from '../httpClients/RepositoryClient/api.schema';
import { QUERY_KEYS } from '../queries/consts';
import { MUTATION_KEYS } from './consts';

/**
 * Mutation hook for the GitHub Marketplace MT installation create endpoint.
 *
 * Persists a marketplace-originated installation against the BE-issued state
 * token. Single-use semantics — the wizard guards against double invocation
 * with an in-flight ref. Errors carry FastAPI `{ detail: '<CODE>' }` shape and
 * are mapped to `MarketplaceErrorCode` by the wizard.
 *
 * On success invalidates the installations count, installations list, and git
 * repositories so views revisited after closing the wizard reflect the newly
 * persisted installation and its associated repositories.
 */
export const useCreateMarketplaceMTGithubInstallationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateMarketplaceMTGithubInstallationResponse,
    unknown,
    CreateMarketplaceMTGithubInstallationRequest
  >({
    mutationKey: [MUTATION_KEYS.CREATE_MT_MARKETPLACE_INSTALLATION],
    mutationFn: RepositoryClient.createMarketplaceMTGithubInstallation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
      });
    },
  });
};
